import React, { useState, useEffect, useRef } from 'react';
import '../styles/DrawingInterface.css';
import ShapeBlock from './ShapeBlock';
import { BlockShape } from './ShapeDefinitions';
import PlacedShape, { PlacedShapeData } from './PlacedShape';
import { PREDEFINED_SHAPES, ELEMENT_COLORS, DEFAULT_COLOR } from './ShapeDefinitions';
import GalleryPage, { GeneratedShape as GalleryGeneratedShape } from './GalleryPage';

// List of narrative elements
const narrativeElements = [
  'Tree',
  'Bridge',
  'Basket',
  'River',
  'Road',
  'House'
];

// Grid size configuration
const GRID_SIZE = 20; // 20x20 grid as per requirement
const CELL_SIZE = 30; // 30px per cell (smaller size)

// Interface for point coordinates
interface Point {
  x: number;
  y: number;
}

// Interface for generated shape
type GeneratedShape = GalleryGeneratedShape;

// Ghost shape for drag preview
interface GhostShape {
  shape: BlockShape;
  row: number;
  col: number;
  isValid: boolean;
}

// Mode types for the drawing interface
type DrawingMode = 'block' | 'pen';

// Create a local storage key for saving shapes
const STORAGE_KEY = 'generatedShapes';
const STORAGE_CHUNK_PREFIX = 'generatedShapes_chunk_';
const MAX_CHUNK_SIZE = 500 * 1024; // 500KB per chunk
const MAX_SHAPES_PER_EXPORT = 50; // Maximum shapes to store before suggesting export

// LZ-based string compression for storage optimization
const compressString = (str: string): string => {
  // Simple run-length encoding for repeated characters
  let compressed = '';
  let count = 1;
  let current = str[0];
  
  for (let i = 1; i <= str.length; i++) {
    if (i === str.length || str[i] !== current) {
      // If run length is 3 or more, use compression format
      if (count >= 3) {
        compressed += `${count}×${current}`;
      } else {
        // Otherwise just repeat the character
        compressed += current.repeat(count);
      }
      count = 1;
      current = str[i];
    } else {
      count++;
    }
  }
  
  // If compression didn't help, return original
  return compressed.length < str.length ? compressed : str;
};

// Decompress the compressed string
const decompressString = (str: string): string => {
  // Check if the string is actually compressed
  if (!str.includes('×')) return str;
  
  // Process run-length encoding
  return str.replace(/(\d+)×(.)/g, (match, count, char) => {
    return char.repeat(parseInt(count, 10));
  });
};

// Function to save data to local storage with chunking and compression
const saveToLocalStorage = (key: string, data: any) => {
  try {
    // Check if we have too many shapes
    if (Array.isArray(data) && data.length > MAX_SHAPES_PER_EXPORT) {
      // Only show warning every 10 shapes after the limit
      if (data.length % 10 === 0) {
        const shouldExport = window.confirm(
          `You now have ${data.length} shapes in your gallery. This may impact performance. ` +
          `Would you like to export your current shapes to a file to free up space?`
        );
        
        if (shouldExport) {
          exportShapesToFile(data);
          return;
        }
      }
    }
    
    // Convert data to string
    const stringData = JSON.stringify(data);
    
    // Compress the string
    const compressedData = compressString(stringData);
    
    // If data is small enough, save directly
    if (compressedData.length < MAX_CHUNK_SIZE) {
      localStorage.setItem(`${key}_compressed`, 'true');
      localStorage.setItem(key, compressedData);
      // Clear any chunks that might exist from previous saves
      clearChunks(key);
      return;
    }
    
    // If data is too large, split into chunks
    const chunks = [];
    let index = 0;
    while (index < compressedData.length) {
      chunks.push(compressedData.substring(index, index + MAX_CHUNK_SIZE));
      index += MAX_CHUNK_SIZE;
    }
    
    // Clear previous data and chunks
    clearChunks(key);
    localStorage.removeItem(key);
    localStorage.removeItem(`${key}_compressed`);
    
    // Save chunks 
    chunks.forEach((chunk, i) => {
      localStorage.setItem(`${STORAGE_CHUNK_PREFIX}${i}`, chunk);
    });
    
    // Save chunk info
    localStorage.setItem(`${key}_chunked`, JSON.stringify({
      chunked: true,
      chunkCount: chunks.length,
      compressed: true
    }));
  } catch (error) {
    console.error('Error saving to localStorage:', error);
  }
};

// Export shapes to a file for backup/storage
const exportShapesToFile = (shapes: GeneratedShape[]) => {
  try {
    const dataStr = JSON.stringify(shapes, null, 2);
    const dataUri = `data:application/json;charset=utf-8,${encodeURIComponent(dataStr)}`;
    
    // Create a download link and trigger it
    const exportName = `shapes-backup-${new Date().toISOString().slice(0, 10)}.json`;
    const linkElement = document.createElement('a');
    linkElement.setAttribute('href', dataUri);
    linkElement.setAttribute('download', exportName);
    linkElement.click();
    linkElement.remove();
  } catch (error) {
    console.error('Error exporting shapes:', error);
    alert('There was an issue exporting your shapes. Please try again.');
  }
};

// Function to retrieve chunked and compressed data from local storage
const retrieveFromLocalStorage = (key: string): any => {
  try {
    // First check if data is chunked
    const chunkInfo = localStorage.getItem(`${key}_chunked`);
    
    if (chunkInfo) {
      // Data is chunked, retrieve all chunks
      const { chunkCount, compressed } = JSON.parse(chunkInfo);
      let fullData = '';
      
      for (let i = 0; i < chunkCount; i++) {
        const chunk = localStorage.getItem(`${STORAGE_CHUNK_PREFIX}${i}`);
        if (chunk) {
          fullData += chunk;
        } else {
          console.error(`Chunk ${i} is missing`);
        }
      }
      
      // Decompress if needed
      const finalData = compressed ? decompressString(fullData) : fullData;
      return JSON.parse(finalData);
    }
    
    // Then check if data is compressed but not chunked
    const isCompressed = localStorage.getItem(`${key}_compressed`) === 'true';
    const data = localStorage.getItem(key);
    
    if (!data) return null;
    
    // Decompress if needed
    const finalData = isCompressed ? decompressString(data) : data;
    return JSON.parse(finalData);
  } catch (error) {
    console.error('Error retrieving from localStorage:', error);
    return null;
  }
};

// Function to clear chunks
const clearChunks = (key: string) => {
  const chunkInfo = localStorage.getItem(`${key}_chunked`);
  if (!chunkInfo) return;
  
  const { chunkCount } = JSON.parse(chunkInfo);
  
  // Remove all chunks
  for (let i = 0; i < chunkCount; i++) {
    localStorage.removeItem(`${STORAGE_CHUNK_PREFIX}${i}`);
  }
  
  // Remove chunk info
  localStorage.removeItem(`${key}_chunked`);
};

// Function to clear all generated shapes and related storage
const clearGeneratedShapes = () => {
  clearChunks(STORAGE_KEY);
  localStorage.removeItem(STORAGE_KEY);
  localStorage.removeItem(`${STORAGE_KEY}_chunked`);
  localStorage.removeItem(`${STORAGE_KEY}_compressed`);
};

// For metaball field calculations
interface FieldPoint {
  x: number;
  y: number;
  value: number;
}

const DrawingInterface: React.FC = () => {
  // State for the selected narrative element (keeping from original)
  const [selectedElement, setSelectedElement] = useState<string | null>(null);
  
  // State for the grid data
  const [drawingGrid, setDrawingGrid] = useState<boolean[][]>(
    Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill(false))
  );
  
  // State for placed shapes
  const [placedShapes, setPlacedShapes] = useState<PlacedShapeData[]>([]);
  
  // State for the selected block shape
  const [selectedBlockShape, setSelectedBlockShape] = useState<BlockShape | null>(null);
  
  // State for the currently selected placed shape
  const [selectedPlacedShape, setSelectedPlacedShape] = useState<string | null>(null);
  
  // State for the ghost shape (drag preview)
  const [ghostShape, setGhostShape] = useState<GhostShape | null>(null);
  
  // State for drawing mode - add this new state
  const [drawingMode, setDrawingMode] = useState<DrawingMode>('block');
  
  // State for tracking if mouse is pressed for drawing with pen
  const [isDrawing, setIsDrawing] = useState<boolean>(false);
  
  // State for generated shapes (keeping from original)
  const [generatedShapes, setGeneratedShapes] = useState<GeneratedShape[]>(() => {
    // Use the new retrieval function
    return retrieveFromLocalStorage(STORAGE_KEY) || [];
  });
  
  // State for showing the gallery page (keeping from original)
  const [showGalleryPage, setShowGalleryPage] = useState(false);
  
  // State for showing gallery prompt after generating
  const [showGalleryPrompt, setShowGalleryPrompt] = useState(false);
  
  // State for showing instructions modal
  const [showInstructions, setShowInstructions] = useState(false);
  
  // Reference to the canvas (keeping from original)
  const canvasRef = useRef<HTMLCanvasElement>(null);
  
  // Reference to the grid container for drag and drop
  const gridContainerRef = useRef<HTMLDivElement>(null);
  
  // State for the metaball preview
  const [, setMetaballPreview] = useState<string>('');
  
  // Save generated shapes to localStorage when they change
  useEffect(() => {
    saveToLocalStorage(STORAGE_KEY, generatedShapes);
  }, [generatedShapes]);
  
  // Initialize and draw the background grid (keeping from original)
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;
    
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    
    ctx.fillStyle = '#FAF3E0';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    
    ctx.strokeStyle = '#C00000';
    ctx.lineWidth = 1.5;
    
    // Draw vertical lines
    for (let x = 0; x <= canvas.width; x += CELL_SIZE) {
      ctx.beginPath();
      ctx.moveTo(x, 0);
      ctx.lineTo(x, canvas.height);
      ctx.stroke();
    }
    
    // Draw horizontal lines
    for (let y = 0; y <= canvas.height; y += CELL_SIZE) {
      ctx.beginPath();
      ctx.moveTo(0, y);
      ctx.lineTo(canvas.width, y);
      ctx.stroke();
    }
  }, []);
  
  // Handle window resize (keeping from original)
  useEffect(() => {
    const handleResize = () => {
      const canvas = canvasRef.current;
      if (!canvas) return;
      
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
      
      const ctx = canvas.getContext('2d');
      if (!ctx) return;
      
      ctx.fillStyle = '#FAF3E0';
      ctx.fillRect(0, 0, canvas.width, canvas.height);
      
      const gridSize = CELL_SIZE;
      
      ctx.strokeStyle = '#C00000';
      ctx.lineWidth = 1.5;
      
      // Draw vertical lines
      for (let x = 0; x <= canvas.width; x += gridSize) {
        ctx.beginPath();
        ctx.moveTo(x, 0);
        ctx.lineTo(x, canvas.height);
        ctx.stroke();
      }
      
      // Draw horizontal lines
      for (let y = 0; y <= canvas.height; y += gridSize) {
        ctx.beginPath();
        ctx.moveTo(0, y);
        ctx.lineTo(canvas.width, y);
        ctx.stroke();
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  // Update document background color (keeping from original)
  useEffect(() => {
    document.body.style.backgroundColor = '#FAF3E0';
    
    return () => {
      document.body.style.backgroundColor = '';
    };
  }, []);
  
  // Function to update the grid based on placed shapes
  const updateGridFromShapes = () => {
    // Start with an empty grid
    const newGrid = Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill(false));
    
    // Apply each placed shape to the grid
    placedShapes.forEach(placedShape => {
      const { shape, row, col, rotation } = placedShape;
      const { matrix } = shape;
      
      // Get rotated matrix
      let rotatedMatrix = matrix;
      if (rotation === 90) {
        const rows = matrix.length;
        const cols = matrix[0].length;
        rotatedMatrix = Array(cols).fill(null).map(() => Array(rows).fill(false));
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            rotatedMatrix[c][rows - 1 - r] = matrix[r][c];
          }
        }
      } else if (rotation === 180) {
        const rows = matrix.length;
        const cols = matrix[0].length;
        rotatedMatrix = Array(rows).fill(null).map(() => Array(cols).fill(false));
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            rotatedMatrix[rows - 1 - r][cols - 1 - c] = matrix[r][c];
          }
        }
      } else if (rotation === 270) {
        const rows = matrix.length;
        const cols = matrix[0].length;
        rotatedMatrix = Array(cols).fill(null).map(() => Array(rows).fill(false));
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            rotatedMatrix[cols - 1 - c][r] = matrix[r][c];
          }
        }
      }
      
      // Apply to grid
      const shapeRows = rotatedMatrix.length;
      const shapeCols = rotatedMatrix[0].length;
      
      for (let r = 0; r < shapeRows; r++) {
        for (let c = 0; c < shapeCols; c++) {
          if (rotatedMatrix[r][c]) {
            const gridRow = row + r;
            const gridCol = col + c;
            
            // Only set if within grid bounds
            if (gridRow >= 0 && gridRow < GRID_SIZE && gridCol >= 0 && gridCol < GRID_SIZE) {
              newGrid[gridRow][gridCol] = true;
            }
          }
        }
      }
    });
    
    setDrawingGrid(newGrid);
  };
  
  // Update grid whenever placed shapes change
  useEffect(() => {
    updateGridFromShapes();
  }, [placedShapes]);
  
  // Handle element selection (keeping from original)
  const handleElementSelect = (element: string) => {
    if (selectedElement === element) {
      setSelectedElement(null);
      return;
    }
    
    // Clear all placed shapes when switching between elements without confirmation
    if (placedShapes.length > 0) {
      setPlacedShapes([]);
      setSelectedPlacedShape(null);
    }
    
    setSelectedElement(element);
  };
  
  // Set drawing mode to pen
  const handlePenSelect = () => {
    setDrawingMode(prevMode => prevMode === 'pen' ? 'block' : 'pen');
    if (drawingMode !== 'pen') {
      setSelectedBlockShape(null);
      setSelectedPlacedShape(null);
    }
  };
  
  // Set drawing mode to block
  const handleBlockModeSelect = () => {
    setDrawingMode('block');
  };
  
  // Handle block shape selection
  const handleBlockShapeSelect = (shape: BlockShape) => {
    // Create a copy of the shape with the current element color
    const coloredShape = {
      ...shape,
      color: selectedElement ? ELEMENT_COLORS[selectedElement] : DEFAULT_COLOR
    };
    setSelectedBlockShape(coloredShape);
    setSelectedPlacedShape(null);
  };
  
  // Handle placed shape selection
  const handlePlacedShapeSelect = (id: string) => {
    setSelectedPlacedShape(id);
    setSelectedBlockShape(null);
  };
  
  // Handle block shape drag start
  const handleShapeDragStart = (shape: BlockShape) => {
    // Create a copy of the shape with the current element color
    const coloredShape = {
      ...shape,
      color: selectedElement ? ELEMENT_COLORS[selectedElement] : DEFAULT_COLOR
    };
    setSelectedBlockShape(coloredShape);
  };
  
  // Check if a shape can be placed at the given position
  const canPlaceShapeAt = (
    shape: BlockShape, 
    row: number, 
    col: number, 
    rotation: 0 | 90 | 180 | 270 = 0,
    excludePlacedShapeId?: string
  ): boolean => {
    const { matrix } = shape;
    
    // Get rotated matrix
    let rotatedMatrix = matrix;
    if (rotation === 90) {
      const rows = matrix.length;
      const cols = matrix[0].length;
      rotatedMatrix = Array(cols).fill(null).map(() => Array(rows).fill(false));
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          rotatedMatrix[c][rows - 1 - r] = matrix[r][c];
        }
      }
    } else if (rotation === 180) {
      const rows = matrix.length;
      const cols = matrix[0].length;
      rotatedMatrix = Array(rows).fill(null).map(() => Array(cols).fill(false));
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          rotatedMatrix[rows - 1 - r][cols - 1 - c] = matrix[r][c];
        }
      }
    } else if (rotation === 270) {
      const rows = matrix.length;
      const cols = matrix[0].length;
      rotatedMatrix = Array(cols).fill(null).map(() => Array(rows).fill(false));
      for (let r = 0; r < rows; r++) {
        for (let c = 0; c < cols; c++) {
          rotatedMatrix[cols - 1 - c][r] = matrix[r][c];
        }
      }
    }
    
    const shapeRows = rotatedMatrix.length;
    const shapeCols = rotatedMatrix[0].length;
    
    // Check if the shape will fit within the grid bounds
    if (row < 0 || col < 0 || row + shapeRows > GRID_SIZE || col + shapeCols > GRID_SIZE) {
      return false;
    }
    
    // Create a grid representation of all currently placed shapes
    const occupiedCells: boolean[][] = Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill(false));
    
    // Mark all cells occupied by existing shapes (except the one being moved)
    placedShapes.forEach(placedShape => {
      // Skip the shape we're moving/rotating if specified
      if (excludePlacedShapeId && placedShape.id === excludePlacedShapeId) {
        return;
      }
      
      const { shape, row, col, rotation } = placedShape;
      
      // Get rotated matrix for placed shape
      let placedRotatedMatrix = shape.matrix;
      if (rotation === 90) {
        const rows = shape.matrix.length;
        const cols = shape.matrix[0].length;
        placedRotatedMatrix = Array(cols).fill(null).map(() => Array(rows).fill(false));
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            placedRotatedMatrix[c][rows - 1 - r] = shape.matrix[r][c];
          }
        }
      } else if (rotation === 180) {
        const rows = shape.matrix.length;
        const cols = shape.matrix[0].length;
        placedRotatedMatrix = Array(rows).fill(null).map(() => Array(cols).fill(false));
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            placedRotatedMatrix[rows - 1 - r][cols - 1 - c] = shape.matrix[r][c];
          }
        }
      } else if (rotation === 270) {
        const rows = shape.matrix.length;
        const cols = shape.matrix[0].length;
        placedRotatedMatrix = Array(cols).fill(null).map(() => Array(rows).fill(false));
        for (let r = 0; r < rows; r++) {
          for (let c = 0; c < cols; c++) {
            placedRotatedMatrix[cols - 1 - c][r] = shape.matrix[r][c];
          }
        }
      }
      
      // Mark cells as occupied
      const placedShapeRows = placedRotatedMatrix.length;
      const placedShapeCols = placedRotatedMatrix[0].length;
      
      for (let r = 0; r < placedShapeRows; r++) {
        for (let c = 0; c < placedShapeCols; c++) {
          if (placedRotatedMatrix[r][c]) {
            const gridRow = placedShape.row + r;
            const gridCol = placedShape.col + c;
            
            // Only mark if within grid bounds
            if (gridRow >= 0 && gridRow < GRID_SIZE && gridCol >= 0 && gridCol < GRID_SIZE) {
              occupiedCells[gridRow][gridCol] = true;
            }
          }
        }
      }
    });
    
    // Check if any part of the shape would overlap with existing shapes
    for (let r = 0; r < shapeRows; r++) {
      for (let c = 0; c < shapeCols; c++) {
        if (rotatedMatrix[r][c]) {
          const gridRow = row + r;
          const gridCol = col + c;
          
          // Check if the cell is already occupied
          if (occupiedCells[gridRow][gridCol]) {
            return false;
          }
        }
      }
    }
    
    return true;
  };
  
  // Drawing grid functions
  const handleCellDrag = (row: number, col: number) => {
    if (!selectedElement) return;
    
    setDrawingGrid(prevGrid => {
      // Only update if the cell is not already filled
      if (!prevGrid[row][col]) {
        const newGrid = [...prevGrid];
        newGrid[row] = [...newGrid[row]];
        newGrid[row][col] = true; // Set cell to true when dragging
        return newGrid;
      }
      return prevGrid;
    });
  };
  
  // Handle grid cell drag over with improved efficiency
  const handleGridDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    
    if (!selectedBlockShape) return;
    
    const gridRect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - gridRect.left;
    const offsetY = e.clientY - gridRect.top;
    
    // Calculate grid cell coordinates - snap to grid
    const col = Math.floor(offsetX / CELL_SIZE);
    const row = Math.floor(offsetY / CELL_SIZE);
    
    // Check if the shape can be placed at this position
    const isValid = canPlaceShapeAt(selectedBlockShape, row, col);
    
    // Only update ghost shape if position changed (reduces rerenders)
    if (!ghostShape || ghostShape.row !== row || ghostShape.col !== col) {
      setGhostShape({
        shape: selectedBlockShape,
        row,
        col,
        isValid
      });
    }
  };
  
  // Handle grid cell drag leave
  const handleGridDragLeave = () => {
    setGhostShape(null);
  };
  
  // Handle grid cell drop
  const handleGridDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    
    if (!selectedBlockShape || !ghostShape) {
      setGhostShape(null);
      return;
    }
    
    const { row, col, isValid } = ghostShape;
    
    // Only place shape if it's valid (doesn't overlap with existing shapes)
    if (!isValid) {
      setGhostShape(null);
      return;
    }
    
    // Create a fresh complete copy of the shape to ensure it retains all properties
    const shapeWithColor = {
      ...selectedBlockShape,
      id: selectedBlockShape.id,
      name: selectedBlockShape.name,
      matrix: [...selectedBlockShape.matrix.map(row => [...row])],
      color: selectedBlockShape.color || DEFAULT_COLOR
    };
    
    // Add the new shape at the exact drop position with guaranteed visibility
    const newShape: PlacedShapeData = {
      id: `shape-${Date.now()}`,
      shape: shapeWithColor,
      row,
      col,
      rotation: 0
    };
    
    // Add to placed shapes
    setPlacedShapes(prev => [...prev, newShape]);
    setGhostShape(null);
  };
  
  // Handle drag end
  const handleDragEnd = () => {
    setGhostShape(null);
  };
  
  // Handle delete placed shape
  const handleDeletePlacedShape = (id: string) => {
    setPlacedShapes(prev => prev.filter(shape => shape.id !== id));
    setSelectedPlacedShape(null);
  };
  
  // Handle rotate placed shape
  const handleRotatePlacedShape = (id: string) => {
    setPlacedShapes(prev => prev.map(shape => {
      if (shape.id === id) {
        // Calculate next rotation (0 -> 90 -> 180 -> 270 -> 0)
        const nextRotation = ((shape.rotation + 90) % 360) as 0 | 90 | 180 | 270;
        
        // Check if the rotation is valid (shape stays within bounds and doesn't overlap)
        if (canPlaceShapeAt(shape.shape, shape.row, shape.col, nextRotation, id)) {
          return { ...shape, rotation: nextRotation };
        }
      }
      return shape;
    }));
  };
  
  // Clear the drawing grid and placed shapes
  const clearGrid = () => {
    setPlacedShapes([]);
    setSelectedPlacedShape(null);
    setDrawingGrid(Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill(false)));
  };
  
  // Fill internal voids in the shape
  const fillInternalVoids = (): boolean[][] => {
    // Create a copy of the drawing grid
    const filledGrid: boolean[][] = Array(GRID_SIZE).fill(null).map((_, row) => 
      Array(GRID_SIZE).fill(null).map((_, col) => drawingGrid[row][col])
    );
    
    // First, identify the connected regions of filled cells
    const visited: boolean[][] = Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill(false));
    const componentMap: number[][] = Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill(-1));
    let componentId = 0;
    
    // Flood fill to find connected filled regions
    const fillFilledComponent = (row: number, col: number, id: number) => {
      if (row < 0 || row >= GRID_SIZE || col < 0 || col >= GRID_SIZE) return;
      if (!filledGrid[row][col] || componentMap[row][col] >= 0) return;
      
      // Mark this cell as part of the current component
      componentMap[row][col] = id;
      
      // Recurse to neighbors (4-connected)
      fillFilledComponent(row + 1, col, id);
      fillFilledComponent(row - 1, col, id);
      fillFilledComponent(row, col + 1, id);
      fillFilledComponent(row, col - 1, id);
    };
    
    // Find filled connected components
    for (let row = 0; row < GRID_SIZE; row++) {
      for (let col = 0; col < GRID_SIZE; col++) {
        if (filledGrid[row][col] && componentMap[row][col] < 0) {
          fillFilledComponent(row, col, componentId++);
        }
      }
    }
    
    // Find the largest connected component
    const componentSizes: number[] = Array(componentId).fill(0);
    for (let row = 0; row < GRID_SIZE; row++) {
      for (let col = 0; col < GRID_SIZE; col++) {
        if (componentMap[row][col] >= 0) {
          componentSizes[componentMap[row][col]]++;
        }
      }
    }
    
    let largestComponent = 0;
    for (let i = 1; i < componentSizes.length; i++) {
      if (componentSizes[i] > componentSizes[largestComponent]) {
        largestComponent = i;
      }
    }
    
    // Now find all empty regions (potential voids)
    const emptyComponentMap: number[][] = Array(GRID_SIZE).fill(null).map(() => Array(GRID_SIZE).fill(-1));
    let emptyComponentId = 0;
    
    // Flood fill to find connected empty regions
    const fillEmptyComponent = (row: number, col: number, id: number) => {
      if (row < 0 || row >= GRID_SIZE || col < 0 || col >= GRID_SIZE) return;
      if (filledGrid[row][col] || emptyComponentMap[row][col] >= 0) return;
      
      // Mark this cell as part of the current empty component
      emptyComponentMap[row][col] = id;
      
      // Recurse to neighbors (4-connected)
      fillEmptyComponent(row + 1, col, id);
      fillEmptyComponent(row - 1, col, id);
      fillEmptyComponent(row, col + 1, id);
      fillEmptyComponent(row, col - 1, id);
    };
    
    // Find empty connected components
    for (let row = 0; row < GRID_SIZE; row++) {
      for (let col = 0; col < GRID_SIZE; col++) {
        if (!filledGrid[row][col] && emptyComponentMap[row][col] < 0) {
          fillEmptyComponent(row, col, emptyComponentId++);
        }
      }
    }
    
    // Identify which empty components are internal voids
    const isExternalComponent: boolean[] = Array(emptyComponentId).fill(false);
    
    // An empty component is external if it touches the grid boundary
    for (let row = 0; row < GRID_SIZE; row++) {
      if (emptyComponentMap[row][0] >= 0) isExternalComponent[emptyComponentMap[row][0]] = true;
      if (emptyComponentMap[row][GRID_SIZE-1] >= 0) isExternalComponent[emptyComponentMap[row][GRID_SIZE-1]] = true;
    }
    for (let col = 0; col < GRID_SIZE; col++) {
      if (emptyComponentMap[0][col] >= 0) isExternalComponent[emptyComponentMap[0][col]] = true;
      if (emptyComponentMap[GRID_SIZE-1][col] >= 0) isExternalComponent[emptyComponentMap[GRID_SIZE-1][col]] = true;
    }
    
    // Fill in all internal voids (non-external empty components)
    for (let row = 0; row < GRID_SIZE; row++) {
      for (let col = 0; col < GRID_SIZE; col++) {
        const emptyId = emptyComponentMap[row][col];
        if (emptyId >= 0 && !isExternalComponent[emptyId]) {
          filledGrid[row][col] = true; // Fill this internal void
        }
      }
    }
    
    // Only keep the largest filled component, remove disconnected regions
    for (let row = 0; row < GRID_SIZE; row++) {
      for (let col = 0; col < GRID_SIZE; col++) {
        if (componentMap[row][col] >= 0 && componentMap[row][col] !== largestComponent) {
          filledGrid[row][col] = false; // Remove disconnected components
        }
      }
    }
    
    return filledGrid;
  };
  
  // Helper function to find the edge points of the filled cells
  const findEdgePoints = (filledCells: { row: number; col: number }[], gridSize: number): Point[] => {
    // Create a grid representation for easier neighbor checking
    const grid: boolean[][] = Array(gridSize).fill(null).map(() => Array(gridSize).fill(false));
    filledCells.forEach(cell => {
      grid[cell.row][cell.col] = true;
    });
    
    // Define directions for checking neighbors (8 directions for smoother edges)
    const directions = [
      [-1, -1], [-1, 0], [-1, 1],
      [0, -1],           [0, 1],
      [1, -1],  [1, 0],  [1, 1]
    ];
    
    // Find all cells that are on the edge (have at least one empty neighbor)
    const edgeCells: { row: number; col: number }[] = [];
    
    filledCells.forEach(cell => {
      const { row, col } = cell;
      
      // Check if this cell has any empty neighbors (making it an edge)
      const hasEmptyNeighbor = directions.some(([dr, dc]) => {
        const newRow = row + dr;
        const newCol = col + dc;
        
        // Check if the neighbor is within bounds
        return (
          newRow >= 0 && newRow < gridSize && 
          newCol >= 0 && newCol < gridSize && 
          !grid[newRow][newCol]
        );
      });
      
      if (hasEmptyNeighbor) {
        edgeCells.push(cell);
      }
    });
    
    // If there are no edge cells or too few, return all filled cells
    if (edgeCells.length < 3) {
      return filledCells.map(cell => ({ 
        x: cell.col + 0.5, 
        y: cell.row + 0.5 
      }));
    }
    
    // Refine edge points to create smoother curves
    // Convert edge cells to edge points (center of cells)
    const edgePoints = edgeCells.map(cell => ({ 
      x: cell.col + 0.5, 
      y: cell.row + 0.5 
    }));
    
    // Sort edge points to create a continuous path around the perimeter
    const sortedEdgePoints = sortPointsClockwise(edgePoints);
    
    return sortedEdgePoints;
  };
  
  // Sort points in clockwise order around their center
  const sortPointsClockwise = (points: Point[]): Point[] => {
    if (points.length <= 3) return points;
    
    // Find the center point
    const center = points.reduce(
      (acc, point) => ({ x: acc.x + point.x, y: acc.y + point.y }),
      { x: 0, y: 0 }
    );
    center.x /= points.length;
    center.y /= points.length;
    
    // Sort points based on their angle from the center
    return [...points].sort((a, b) => {
      const angleA = Math.atan2(a.y - center.y, a.x - center.x);
      const angleB = Math.atan2(b.y - center.y, b.x - center.x);
      return angleA - angleB;
    });
  };
  
  // Interpolate points for smoother curves with fewer control points
  const interpolatePoints = (points: Point[], numDivisions: number = 2): Point[] => {
    if (points.length < 2) return points;
    if (points.length === 2) {
      // For just 2 points, create a simple line with multiple points
      const result: Point[] = [];
      const [p0, p1] = points;
      for (let i = 0; i <= numDivisions; i++) {
        const t = i / numDivisions;
        result.push({
          x: p0.x + (p1.x - p0.x) * t,
          y: p0.y + (p1.y - p0.y) * t
        });
      }
      return result;
    }
    
    // Create a smooth curve with limited points by selecting a subset
    // This helps in creating cleaner, smoother shapes with fewer control points
    const n = points.length;
    const step = Math.max(1, Math.floor(n / Math.min(n, 10))); // At most 10 points
    
    let smoothedPoints: Point[] = [];
    
    // Always include the first point
    smoothedPoints.push(points[0]);
    
    // Add significant points along the contour
    for (let i = step; i < n - 1; i += step) {
      smoothedPoints.push(points[i]);
    }
    
    // Always include the last point
    if (smoothedPoints[smoothedPoints.length - 1] !== points[n - 1]) {
      smoothedPoints.push(points[n - 1]);
    }
    
    return smoothedPoints;
  };
  
  // Modified smoothCorners function with controllable corner cutting factor
  const smoothCorners = (points: Point[], cutFactor: number = 0.25): Point[] => {
    if (points.length <= 2) return points;
    
    const result: Point[] = [];
    const n = points.length;
    
    for (let i = 0; i < n - 1; i++) {
      const p0 = points[i];
      const p1 = points[i + 1];
      
      // Create two new points with adjustable cutting factor
      const q0 = {
        x: p0.x * (1 - cutFactor) + p1.x * cutFactor,
        y: p0.y * (1 - cutFactor) + p1.y * cutFactor
      };
      
      const q1 = {
        x: p0.x * cutFactor + p1.x * (1 - cutFactor),
        y: p0.y * cutFactor + p1.y * (1 - cutFactor)
      };
      
      result.push(q0);
      result.push(q1);
    }
    
    // For closed curves, connect the last and first points
    if (Math.abs(points[0].x - points[n-1].x) < 0.1 && 
        Math.abs(points[0].y - points[n-1].y) < 0.1) {
      // Replace the last point with the first point to ensure perfect closure
      result[result.length - 1] = result[0];
    }
    
    return result;
  };
  
  // Helper for Catmull-Rom interpolation
  const getTForAlpha = (p1: Point, p2: Point, alpha: number): number => {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const distance = Math.sqrt(dx * dx + dy * dy);
    return Math.pow(distance, alpha);
  };
  
  // Creates SVG path from points
  const createPath = (points: Point[], cellSize: number, scale: number = 1): string => {
    if (points.length < 2) return '';
    
    // Start with the first point
    let path = `M ${points[0].x * cellSize * scale} ${points[0].y * cellSize * scale}`;
    
    // Add cubic Bezier curves between each set of points
    const tension = 0.4; // Lower value for smoother curves (0.4 matches Illustrator's default)
    
    for (let i = 0; i < points.length - 1; i++) {
      const current = points[i];
      const next = points[i + 1];
      const nextNext = points[(i + 2) % points.length];
      
      // Calculate control points with tension
      const cp1 = {
        x: current.x + (next.x - current.x) * tension,
        y: current.y + (next.y - current.y) * tension
      };
      
      const cp2 = {
        x: next.x - (nextNext.x - current.x) * tension,
        y: next.y - (nextNext.y - current.y) * tension
      };
      
      // Add the curve command to the path
      path += ` C ${cp1.x * cellSize * scale} ${cp1.y * cellSize * scale}, ${cp2.x * cellSize * scale} ${cp2.y * cellSize * scale}, ${next.x * cellSize * scale} ${next.y * cellSize * scale}`;
    }
    
    // Close the path if there are more than 2 points
    if (points.length > 2) {
      path += ' Z';
    }
    
    return path;
  };
  
  // Sample the metaball field at a specific point
  const sampleMetaballField = (
    x: number, 
    y: number, 
    shapes: PlacedShapeData[], 
    cellSize: number
  ): number => {
    // Accumulate field values from all shapes
    let fieldValue = 0;
    
    shapes.forEach(placedShape => {
      const { shape, row, col, rotation } = placedShape;
      const { matrix } = shape;
      
      // Apply rotation and calculate shape dimensions
      let rotatedMatrix = matrix;
      if (rotation > 0) {
        // ... existing rotation code ...
        const rows = matrix.length;
        const cols = matrix[0].length;
        
        if (rotation === 90) {
          rotatedMatrix = Array(cols).fill(null).map(() => Array(rows).fill(false));
          for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
              rotatedMatrix[c][rows - 1 - r] = matrix[r][c];
            }
          }
        } else if (rotation === 180) {
          rotatedMatrix = Array(rows).fill(null).map(() => Array(cols).fill(false));
          for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
              rotatedMatrix[rows - 1 - r][cols - 1 - c] = matrix[r][c];
            }
          }
        } else if (rotation === 270) {
          rotatedMatrix = Array(cols).fill(null).map(() => Array(rows).fill(false));
          for (let r = 0; r < rows; r++) {
            for (let c = 0; c < cols; c++) {
              rotatedMatrix[cols - 1 - c][r] = matrix[r][c];
            }
          }
        }
      }
      
      // For each filled cell in the shape, add its contribution to the field
      const shapeRows = rotatedMatrix.length;
      const shapeCols = rotatedMatrix[0].length;
      
      for (let r = 0; r < shapeRows; r++) {
        for (let c = 0; c < shapeCols; c++) {
          if (rotatedMatrix[r][c]) {
            // Calculate center of the filled cell
            const cellCenterX = (col + c + 0.5) * cellSize;
            const cellCenterY = (row + r + 0.5) * cellSize;
            
            // Calculate distance from the field point to the cell center
            const dx = x - cellCenterX;
            const dy = y - cellCenterY;
            const distanceSquared = dx * dx + dy * dy;
            
            // Gaussian-like falloff function
            const radius = cellSize * 0.75; // Same as placed-cell size
            const radiusSquared = radius * radius;
            
            // Calculate falloff based on distance
            // Value is highest at cell center, falls off with distance
            if (distanceSquared < radiusSquared * 4) {
              // Adjust the falloff factor to control how much shapes blend
              const falloff = Math.exp(-distanceSquared / (2 * radiusSquared));
              fieldValue += falloff;
            }
          }
        }
      }
    });
    
    return fieldValue;
  };
  
  // Extract contour from the metaball field using marching squares
  const extractMetaballContour = (
    shapes: PlacedShapeData[],
    cellSize: number,
    gridSize: number,
    resolution: number = 2, // Reduced resolution for smoother outline
    threshold: number = 0.5  // Field value threshold for contour
  ): Point[] => {
    if (shapes.length === 0) return [];
    
    // Determine the field bounds by finding the min/max positions of all shapes
    let minRow = Infinity, minCol = Infinity;
    let maxRow = -Infinity, maxCol = -Infinity;
    
    shapes.forEach(placedShape => {
      const { row, col, shape, rotation } = placedShape;
      
      // Get rotated matrix dimensions
      let rows = shape.matrix.length;
      let cols = shape.matrix[0].length;
      
      if (rotation === 90 || rotation === 270) {
        // Swap dimensions for 90 and 270 degree rotations
        [rows, cols] = [cols, rows];
      }
      
      minRow = Math.min(minRow, row);
      minCol = Math.min(minCol, col);
      maxRow = Math.max(maxRow, row + rows);
      maxCol = Math.max(maxCol, col + cols);
    });
    
    // Add padding to ensure we capture the full contour
    minRow = Math.max(0, minRow - 2);
    minCol = Math.max(0, minCol - 2);
    maxRow = Math.min(gridSize, maxRow + 2);
    maxCol = Math.min(gridSize, maxCol + 2);
    
    // Calculate field resolution
    const step = 1 / resolution;
    const width = (maxCol - minCol) * resolution + 1;
    const height = (maxRow - minRow) * resolution + 1;
    
    // Sample the field at regular grid points
    const field: FieldPoint[][] = [];
    
    for (let j = 0; j <= height; j++) {
      const row: FieldPoint[] = [];
      const y = (minRow + j * step) * cellSize;
      
      for (let i = 0; i <= width; i++) {
        const x = (minCol + i * step) * cellSize;
        const value = sampleMetaballField(x, y, shapes, cellSize);
        row.push({ x, y, value });
      }
      
      field.push(row);
    }
    
    // Use marching squares to extract the contour
    const contourPoints: Point[] = marchingSquares(field, threshold);
    
    // Sort the contour points to ensure they form a continuous path
    return sortPointsClockwise(contourPoints);
  };
  
  // Marching squares algorithm to extract contour points
  const marchingSquares = (field: FieldPoint[][], threshold: number): Point[] => {
    const contourPoints: Point[] = [];
    
    // Process each cell in the field
    for (let j = 0; j < field.length - 1; j++) {
      for (let i = 0; i < field[j].length - 1; i++) {
        // Get the 4 corners of the current cell
        const topLeft = field[j][i];
        const topRight = field[j][i + 1];
        const bottomLeft = field[j + 1][i];
        const bottomRight = field[j + 1][i + 1];
        
        // Determine the case based on which corners are inside/outside the contour
        let caseIndex = 0;
        if (topLeft.value > threshold) caseIndex |= 8;
        if (topRight.value > threshold) caseIndex |= 4;
        if (bottomRight.value > threshold) caseIndex |= 2;
        if (bottomLeft.value > threshold) caseIndex |= 1;
        
        // Skip empty cells
        if (caseIndex === 0 || caseIndex === 15) continue;
        
        // Calculate interpolated points along the edges
        const points: Point[] = [];
        
        // Top edge (between topLeft and topRight)
        if ((caseIndex & 12) === 4 || (caseIndex & 12) === 8) {
          const t = (threshold - topLeft.value) / (topRight.value - topLeft.value);
          points.push({
            x: topLeft.x + t * (topRight.x - topLeft.x),
            y: topLeft.y
          });
        }
        
        // Right edge (between topRight and bottomRight)
        if ((caseIndex & 6) === 2 || (caseIndex & 6) === 4) {
          const t = (threshold - topRight.value) / (bottomRight.value - topRight.value);
          points.push({
            x: topRight.x,
            y: topRight.y + t * (bottomRight.y - topRight.y)
          });
        }
        
        // Bottom edge (between bottomLeft and bottomRight)
        if ((caseIndex & 3) === 1 || (caseIndex & 3) === 2) {
          const t = (threshold - bottomLeft.value) / (bottomRight.value - bottomLeft.value);
          points.push({
            x: bottomLeft.x + t * (bottomRight.x - bottomLeft.x),
            y: bottomLeft.y
          });
        }
        
        // Left edge (between topLeft and bottomLeft)
        if ((caseIndex & 9) === 1 || (caseIndex & 9) === 8) {
          const t = (threshold - topLeft.value) / (bottomLeft.value - topLeft.value);
          points.push({
            x: topLeft.x,
            y: topLeft.y + t * (bottomLeft.y - topLeft.y)
          });
        }
        
        // For saddle points (case 5 and 10), we need to decide how to connect the points
        if (caseIndex === 5 || caseIndex === 10) {
          // Use the average value at the center to determine the connection
          const centerValue = (topLeft.value + topRight.value + bottomLeft.value + bottomRight.value) / 4;
          
          // If the center value is higher than the threshold, invert the connection
          if ((centerValue > threshold && caseIndex === 5) || 
              (centerValue <= threshold && caseIndex === 10)) {
            // Swap the order of points to change the connection
            if (points.length === 4) {
              const temp = points[1];
              points[1] = points[2];
              points[2] = temp;
            }
          }
        }
        
        // Add the points to the contour
        for (const point of points) {
          contourPoints.push(point);
        }
      }
    }
    
    return contourPoints;
  };
  
  // Update the metaball preview effect to not render anything
  useEffect(() => {
    // This effect is now disabled since we want to hide outlines
    setMetaballPreview('');
  }, [placedShapes]);
  
  // Merge points that are too close to each other based on a threshold distance
  const mergeClosePoints = (points: Point[], threshold: number = 2.0): Point[] => {
    if (points.length <= 2) return points;
    
    const mergedPoints: Point[] = [];
    let lastAddedPoint: Point | null = null;
    
    for (let i = 0; i < points.length; i++) {
      const currentPoint = points[i];
      
      // Check if this point is too close to the last added point
      if (lastAddedPoint) {
        const dx = currentPoint.x - lastAddedPoint.x;
        const dy = currentPoint.y - lastAddedPoint.y;
        const distance = Math.sqrt(dx * dx + dy * dy);
        
        // If too close, merge by taking the average position
        if (distance < threshold) {
          // Update the last point position instead of adding a new one
          lastAddedPoint.x = (lastAddedPoint.x + currentPoint.x) / 2;
          lastAddedPoint.y = (lastAddedPoint.y + currentPoint.y) / 2;
          continue;
        }
      }
      
      // Add the current point
      mergedPoints.push(currentPoint);
      lastAddedPoint = currentPoint;
    }
    
    // Ensure we close the path by checking first and last points
    if (mergedPoints.length > 2) {
      const first = mergedPoints[0];
      const last = mergedPoints[mergedPoints.length - 1];
      
      const dx = last.x - first.x;
      const dy = last.y - first.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      // If first and last points are close enough, remove the last point to avoid tiny segments
      if (distance < threshold * 1.2) {
        mergedPoints.pop();
      } 
      // If the distance is too large, add a connecting point to close the shape
      else if (distance > threshold * 3) {
        // Don't add an extra point - we'll handle curve closing in createCircularPath
      }
    }
    
    return mergedPoints;
  };
  
  // Enhanced Bauhaus-style path creation with smoother curves and random variation
  const createCircularPath = (points: Point[], cellSize: number, scale: number = 1): string => {
    if (points.length < 2) return '';
    if (points.length === 2) {
      // For just 2 points, create a simple closed shape (small ellipse)
      const x1 = points[0].x * cellSize * scale;
      const y1 = points[0].y * cellSize * scale;
      const x2 = points[1].x * cellSize * scale;
      const y2 = points[1].y * cellSize * scale;
      const midX = (x1 + x2) / 2;
      const midY = (y1 + y2) / 2;
      const dx = x2 - x1;
      const dy = y2 - y1;
      const dist = Math.sqrt(dx * dx + dy * dy);
      
      // Create an elliptical path that passes through both points
      return `M ${x1} ${y1} Q ${midX + dist/4} ${midY - dist/4}, ${x2} ${y2} Q ${midX - dist/4} ${midY + dist/4}, ${x1} ${y1} Z`;
    }
    
    // Add very subtle random noise to make curves more natural, but keep geometric precision
    const noisyPoints = points.map(point => {
      // Reduced random variation (1.5% of cell size) for more geometric precision
      const noiseAmount = cellSize * 0.015;
      return {
        x: point.x + (Math.random() * 2 - 1) * noiseAmount,
        y: point.y + (Math.random() * 2 - 1) * noiseAmount
      };
    });
    
    // Analyze segment lengths to determine appropriate tension values
    const segmentLengths: number[] = [];
    for (let i = 0; i < noisyPoints.length - 1; i++) {
      const current = noisyPoints[i];
      const next = noisyPoints[i + 1];
      const dx = next.x - current.x;
      const dy = next.y - current.y;
      segmentLengths.push(Math.sqrt(dx * dx + dy * dy));
    }
    
    // Calculate average segment length
    const avgSegmentLength = segmentLengths.reduce((sum, len) => sum + len, 0) / segmentLengths.length;
    
    // Start with the first point
    let path = `M ${noisyPoints[0].x * cellSize * scale} ${noisyPoints[0].y * cellSize * scale}`;
    
    // Track previous control points for ultra-smooth transitions
    let previousCP2: Point | null = null;
    
    for (let i = 0; i < noisyPoints.length - 1; i++) {
      const current = noisyPoints[i];
      const next = noisyPoints[i + 1];
      const nextNext = noisyPoints[(i + 2) % noisyPoints.length];
      const prevPoint = noisyPoints[(i - 1 + noisyPoints.length) % noisyPoints.length];
      
      // Calculate segment length and adjust tension accordingly
      const segmentLength = segmentLengths[i];
      // Use more tension for longer segments, less for shorter ones
      const tensionFactor = Math.min(1.0, Math.max(0.5, segmentLength / avgSegmentLength));
      const tension = 0.35 * tensionFactor; // Lower base tension for smoother curves
      
      // Calculate direction vectors for smoother control point placement
      const prevDir = {
        x: current.x - prevPoint.x,
        y: current.y - prevPoint.y
      };
      const nextDir = {
        x: nextNext.x - next.x,
        y: nextNext.y - next.y
      };
      
      // First control point should mirror the last control point from previous segment
      const cp1 = previousCP2 ? {
        x: 2 * current.x - previousCP2.x,
        y: 2 * current.y - previousCP2.y
      } : {
        x: current.x + prevDir.x * tension,
        y: current.y + prevDir.y * tension
      };
      
      // Second control point considers the next segment direction
      const cp2 = {
        x: next.x - nextDir.x * tension,
        y: next.y - nextDir.y * tension
      };
      
      // Add the curve command to the path
      path += ` C ${cp1.x * cellSize * scale} ${cp1.y * cellSize * scale}, ${cp2.x * cellSize * scale} ${cp2.y * cellSize * scale}, ${next.x * cellSize * scale} ${next.y * cellSize * scale}`;
      
      // Save this control point for the next segment
      previousCP2 = cp2;
    }
    
    // Always close the path for a complete shape
    // Check if we need to add a connecting curve back to the start
    if (noisyPoints.length > 2) {
      const first = noisyPoints[0];
      const last = noisyPoints[noisyPoints.length - 1];
      const dx = last.x - first.x;
      const dy = last.y - first.y;
      const distance = Math.sqrt(dx * dx + dy * dy);
      
      // Different approach based on whether the first and last points are already close
      if (distance > avgSegmentLength * 0.7) {
        // Points are far apart - add a smooth connecting curve
        const beforeFirst = noisyPoints[noisyPoints.length - 1];
        const afterFirst = noisyPoints[1];
        
        // Calculate control points for a smooth closing curve
        const tension = 0.35;
        
        // Direction vectors for control points
        const dirToFirst = {
          x: first.x - beforeFirst.x,
          y: first.y - beforeFirst.y
        };
        
        const dirFromFirst = {
          x: afterFirst.x - first.x,
          y: afterFirst.y - first.y
        };
        
        // Control point coming into the first point
        const cp1 = {
          x: last.x + dirToFirst.x * tension,
          y: last.y + dirToFirst.y * tension
        };
        
        // Control point going out from the first point
        const cp2 = {
          x: first.x + dirFromFirst.x * tension * 0.5,
          y: first.y + dirFromFirst.y * tension * 0.5
        };
        
        // Add connecting curve and close the path
        path += ` C ${cp1.x * cellSize * scale} ${cp1.y * cellSize * scale}, ${cp2.x * cellSize * scale} ${cp2.y * cellSize * scale}, ${first.x * cellSize * scale} ${first.y * cellSize * scale}`;
      }
    }
    
    // Always close the path
    path += ' Z';
    
    return path;
  };
  
  // Enhanced handleGenerate function with improved Bauhaus-style shape generation
  const handleGenerate = () => {
    if (placedShapes.length < 1) {
      alert('Place at least one shape to generate');
      return;
    }
    
    // Extract contour with higher resolution for smoother Bauhaus shapes
    const contourPoints = extractMetaballContour(placedShapes, CELL_SIZE, GRID_SIZE, 6, 0.5);
    
    if (contourPoints.length < 3) {
      alert('Could not generate a valid shape contour');
      return;
    }
    
    // Create a smooth curve with fewer points for simpler shapes
    const smoothPoints = interpolatePoints(contourPoints, 3);
    
    // Merge points that are too close to avoid tiny segments and overlapping
    const mergedPoints = mergeClosePoints(smoothPoints, 2.0);
    
    // Create SVG path with enhanced geometric precision and ensure it's closed
    const finalPathData = createCircularPath(mergedPoints, 1, 1);
    
    // Use the selected element type
    const elementType = selectedElement || 'Shape';
    
    // Get the color based on the element type
    const elementColor = selectedElement ? ELEMENT_COLORS[selectedElement] : DEFAULT_COLOR;
    
    // Generate shape ID with timestamp for uniqueness
    const shapeId = `shape-${Date.now()}`;
    
    // Create a new generated shape
    const newShape: GeneratedShape = {
      id: shapeId,
      elementType,
      pathData: finalPathData,
      points: mergedPoints.length,
      createdAt: new Date().toISOString(),
      color: elementColor
    };
    
    // Add to generated shapes
    setGeneratedShapes(prev => {
      const updatedShapes = [newShape, ...prev];
      saveToLocalStorage(STORAGE_KEY, updatedShapes);
      return updatedShapes;
    });
    
    // Directly go to gallery page
    setShowGalleryPage(true);
  };

  // Function to view gallery
  const handleViewGallery = () => {
    setShowGalleryPage(true);
  };
  
  // Handle gallery prompt
  useEffect(() => {
    if (showGalleryPrompt) {
      const shouldViewGallery = window.confirm('Shape generated! View in gallery?');
      if (shouldViewGallery) {
        setShowGalleryPage(true);
      }
      setShowGalleryPrompt(false);
    }
  }, [showGalleryPrompt]);
  
  // Clear all shapes - update to use the new clearGeneratedShapes function
  const handleClearAllShapes = () => {
    if (window.confirm('Are you sure you want to clear all your generated shapes? This cannot be undone.')) {
      clearGeneratedShapes();
      setGeneratedShapes([]);
    }
  };
  
  // Handle importing shapes from file
  const handleImportShapes = (importedShapes: GeneratedShape[]) => {
    if (importedShapes.length === 0) {
      alert('No valid shapes found in the imported file.');
      return;
    }
    
    // Ask user how to handle the imported shapes
    const option = window.confirm(
      `Found ${importedShapes.length} shapes in the imported file.\n\n` +
      'Click OK to add these shapes to your current gallery.\n' +
      'Click Cancel to replace your current gallery with these shapes.'
    );
    
    if (option) {
      // Add to existing shapes
      setGeneratedShapes(prevShapes => {
        // Identify duplicates by ID
        const existingIds = new Set(prevShapes.map(shape => shape.id));
        // Only add shapes that don't already exist
        const newShapes = importedShapes.filter(shape => !existingIds.has(shape.id));
        
        if (newShapes.length === 0) {
          alert('All shapes in the imported file already exist in your gallery.');
          return prevShapes;
        }
        
        const updatedShapes = [...newShapes, ...prevShapes];
        saveToLocalStorage(STORAGE_KEY, updatedShapes);
        
        alert(`Added ${newShapes.length} new shapes to your gallery.`);
        return updatedShapes;
      });
    } else {
      // Replace existing shapes
      setGeneratedShapes(importedShapes);
      saveToLocalStorage(STORAGE_KEY, importedShapes);
      alert(`Replaced your gallery with ${importedShapes.length} imported shapes.`);
    }
  };
  
  // Handle click on grid to deselect shape when clicking on blank areas
  const handleGridClick = (e: React.MouseEvent<HTMLDivElement>) => {
    // Get the clicked element
    const clickedElement = e.target as HTMLElement;
    
    // Check if the click is on the grid itself, not on a placed shape
    if (clickedElement.classList.contains('drawing-grid') || 
        clickedElement.classList.contains('blank-canvas')) {
      setSelectedPlacedShape(null);
    }
    // We don't want to interfere with normal shape selection handled by PlacedShape component
  };
  
  // Handle mouse down on grid for pen drawing
  const handleMouseDown = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only process if in pen mode and an element is selected
    if (drawingMode !== 'pen' || !selectedElement) return;
    
    const gridRect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - gridRect.left;
    const offsetY = e.clientY - gridRect.top;
    
    // Calculate grid cell coordinates
    const col = Math.floor(offsetX / CELL_SIZE);
    const row = Math.floor(offsetY / CELL_SIZE);
    
    // Check if within bounds
    if (row >= 0 && row < GRID_SIZE && col >= 0 && col < GRID_SIZE) {
      // Start drawing
      setIsDrawing(true);
      
      // Create a shape with a single cell
      const matrix = Array(1).fill(null).map(() => Array(1).fill(true));
      const penShape: BlockShape = {
        id: `pen-${Date.now()}`,
        name: 'Pen',
        matrix,
        color: selectedElement ? ELEMENT_COLORS[selectedElement] : DEFAULT_COLOR
      };
      
      // Place the shape at the clicked position
      const newShape: PlacedShapeData = {
        id: `pen-shape-${Date.now()}`,
        shape: penShape,
        row,
        col,
        rotation: 0
      };
      
      // Add to placed shapes
      setPlacedShapes(prev => [...prev, newShape]);
    }
  };
  
  // Handle mouse move for continuous drawing
  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    // Only process if actively drawing in pen mode
    if (!isDrawing || drawingMode !== 'pen' || !selectedElement) return;
    
    const gridRect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - gridRect.left;
    const offsetY = e.clientY - gridRect.top;
    
    // Calculate grid cell coordinates
    const col = Math.floor(offsetX / CELL_SIZE);
    const row = Math.floor(offsetY / CELL_SIZE);
    
    // Check if within bounds
    if (row >= 0 && row < GRID_SIZE && col >= 0 && col < GRID_SIZE) {
      // Check if this cell already has a shape from our current drawing session
      const cellHasPenShape = placedShapes.some(shape => 
        shape.id.startsWith('pen-shape-') && 
        shape.row === row && 
        shape.col === col
      );
      
      // If not already drawn in this session, add a new pen shape
      if (!cellHasPenShape) {
        // Create a shape with a single cell
        const matrix = Array(1).fill(null).map(() => Array(1).fill(true));
        const penShape: BlockShape = {
          id: `pen-${Date.now()}`,
          name: 'Pen',
          matrix,
          color: selectedElement ? ELEMENT_COLORS[selectedElement] : DEFAULT_COLOR
        };
        
        // Place the shape at the current position
        const newShape: PlacedShapeData = {
          id: `pen-shape-${Date.now()}`,
          shape: penShape,
          row,
          col,
          rotation: 0
        };
        
        // Add to placed shapes
        setPlacedShapes(prev => [...prev, newShape]);
      }
    }
  };
  
  // Handle mouse up to stop drawing
  const handleMouseUp = () => {
    setIsDrawing(false);
  };
  
  // Add back the handleBackToDrawing function
  const handleBackToDrawing = () => {
    setShowGalleryPage(false);
  };
  
  // Toggle instructions modal
  const toggleInstructions = () => {
    setShowInstructions(prev => !prev);
  };
  
  // Enhanced function to add Bauhaus geometric modifiers to the path
  const addBauhausModifiers = (pathData: string): string => {
    // For Bauhaus style, we'll maintain the geometric precision of the path
    // This function preserves the clean lines and curves characteristic of Bauhaus design
    return pathData;
  };
  
  // If the gallery page is shown, render it instead of the drawing interface
  if (showGalleryPage) {
    return (
      <GalleryPage 
        shapes={generatedShapes}
        onBack={handleBackToDrawing}
        onClearAll={handleClearAllShapes}
        onImport={handleImportShapes}
      />
    );
  }
  
  // Render the drawing interface
  return (
    <div className="bauhaus-interface">
      <canvas
        ref={canvasRef}
        className="grid-canvas"
      />
      
      {/* Drawing instruction at the top */}
      <div className="drawing-instruction">
        {selectedElement ? (
          <p>You are drawing a <span className="element-highlight">{selectedElement}</span></p>
        ) : (
          <p>Select an element type and arrange building blocks</p>
        )}
      </div>
      
      {/* Instruction link in upper left */}
      <div className="instruction-link" onClick={toggleInstructions}>
        Instruction
      </div>
      
      {/* Gallery link in upper right */}
      <div className="gallery-link" onClick={handleViewGallery}>
        View Gallery
      </div>
      
      {/* Element panel in lower left */}
      <div className="element-panel">
        <div className="element-buttons">
          {narrativeElements.map((element) => (
            <button 
              key={element}
              className={`element-button ${selectedElement === element ? 'selected' : ''}`}
              onClick={() => handleElementSelect(element)}
              data-element={element}
            >
              {element}
            </button>
          ))}
        </div>
      </div>
      
      {/* Shape blocks container - moved to bottom center */}
      <div className="shape-blocks-container">
        {/* Pen toggle button first (on the left) */}
        <button 
          className={`pen-toggle-button ${drawingMode === 'pen' ? 'active' : ''}`}
          onClick={handlePenSelect}
          title={drawingMode === 'pen' ? 'Switch to Block Mode' : 'Switch to Pen Tool'}
        >
          {/* Using CSS for pen icon now */}
        </button>
        
        {/* Shape blocks in the middle */}
        <div className="shape-blocks-grid">
          {PREDEFINED_SHAPES.map((shape) => (
            <ShapeBlock 
              key={shape.id}
              shape={shape}
              onDragStart={handleShapeDragStart}
              selected={selectedBlockShape?.id === shape.id}
              onClick={handleBlockShapeSelect}
              selectedElement={selectedElement}
            />
          ))}
        </div>
        
        {/* Buttons now placed to the right side of all blocks */}
        <div className="blocks-action-buttons">
          <button 
            className="action-button generate-button"
            onClick={handleGenerate}
            disabled={!selectedElement}
          >
            Generate
          </button>
          
          <button 
            className="action-button clear-button"
            onClick={clearGrid}
          >
            Clear All
          </button>
        </div>
      </div>
      
      {/* Main drawing area (in center) */}
      <div className="drawing-area">
        <div 
          className={`drawing-grid-container ${drawingMode === 'pen' ? 'pen-mode' : 'block-mode'}`}
          ref={gridContainerRef}
          onDragOver={handleGridDragOver}
          onDragLeave={handleGridDragLeave}
          onDrop={handleGridDrop}
          onDragEnd={handleDragEnd}
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
        >
          <div 
            className="drawing-grid blank-canvas"
            onClick={handleGridClick}
          >
            {/* Remove grid cells visualization, keep only a blank canvas */}
            
            {/* Render placed shapes */}
            {placedShapes.map((placedShape) => (
              <PlacedShape
                key={placedShape.id}
                data={placedShape}
                isSelected={selectedPlacedShape === placedShape.id}
                onClick={handlePlacedShapeSelect}
                onDelete={handleDeletePlacedShape}
                onRotate={handleRotatePlacedShape}
              />
            ))}
            
            {/* Ghost shape for drag preview */}
            {ghostShape && (
              <div 
                className="ghost-shape"
                style={{
                  top: `${ghostShape.row * CELL_SIZE}px`,
                  left: `${ghostShape.col * CELL_SIZE}px`,
                  position: 'absolute',
                  width: `${ghostShape.shape.matrix[0].length * CELL_SIZE}px`,
                  height: `${ghostShape.shape.matrix.length * CELL_SIZE}px`,
                  zIndex: 25,
                }}
              >
                {/* Render individual cells for the ghost shape based on the matrix */}
                {ghostShape.shape.matrix.map((matrixRow, rowIndex) => 
                  matrixRow.map((cell, colIndex) => 
                    cell && (
                      <div
                        key={`ghost-${rowIndex}-${colIndex}`}
                        className={`ghost-cell ${ghostShape.isValid ? 'valid' : 'invalid'}`}
                        style={{
                          position: 'absolute',
                          top: `${rowIndex * CELL_SIZE}px`,
                          left: `${colIndex * CELL_SIZE}px`,
                          width: `${CELL_SIZE}px`,
                          height: `${CELL_SIZE}px`,
                          backgroundColor: ghostShape.isValid ? ghostShape.shape.color : 'rgba(255, 0, 0, 0.5)',
                          opacity: ghostShape.isValid ? 0.7 : 0.5,
                        }}
                      />
                    )
                  )
                )}
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Instruction text at bottom */}
      <div className="instruction-text">
        drag and drop the building blocks
      </div>
      
      {/* Instructions Modal */}
      {showInstructions && (
        <div className="instructions-modal-overlay" onClick={toggleInstructions}>
          <div className="instructions-modal" onClick={(e) => e.stopPropagation()}>
            <h2>What's your Little Red?</h2>
            <ol>
              <li>
                <span className="step-highlight">Pick</span> an element (Tree, Bridge, etc.)
              </li>
              <li>
                <span className="step-highlight">Drag</span> shapes to the canvas or use the <span className="step-highlight">pen tool</span>
              </li>
              <li>
                <span className="step-highlight">Click Generate</span> to create your shape
              </li>
              <li>
                <span className="step-highlight">View</span> your shapes in the Gallery
              </li>
              <li>
                <span className="step-highlight">Export</span> your selected shapes as SVG
              </li>
            </ol>
            <button className="close-instructions-button" onClick={toggleInstructions}>×</button>
          </div>
        </div>
      )}
    </div>
  );
};

export default DrawingInterface; 